/**
 * The parts of a file that have arrived, by their offset in it.
 *
 * Chunks come back out of order — the stretch of video on screen is fetched
 * before the rest — so this cannot be a list of chunks in arrival order. Spans
 * are held sorted and merged as they meet, which keeps the common case (a file
 * filling in from a few directions at once) down to a handful of entries.
 *
 * Pure apart from holding the bytes: no network, no DOM.
 */
export const createByteStore = () => {
  // Ascending, non-overlapping, non-touching: { start, data }.
  let spans = [];

  const endOf = (span) => span.start + span.data.byteLength;

  const indexFor = (offset) => {
    let low = 0;
    let high = spans.length - 1;
    let found = -1;
    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      if (spans[mid].start <= offset) {
        found = mid;
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }
    return found;
  };

  return {
    /** Bytes held, which is NOT the file size while there are gaps. */
    get held() {
      return spans.reduce((sum, span) => sum + span.data.byteLength, 0);
    },

    /** The spans held, for a caller that needs to know what is missing. */
    get ranges() {
      return spans.map((span) => ({ start: span.start, end: endOf(span) }));
    },

    /**
     * Take `data` as the bytes at `start`. Overlapping an existing span is
     * allowed — a re-fetch of something already held is dropped rather than
     * duplicated.
     */
    add(start, data) {
      if (!data || !data.byteLength) return;
      const next = [];
      let from = start;
      let bytes = data;
      spans.forEach((span) => {
        if (endOf(span) < from || span.start > from + bytes.byteLength) {
          next.push(span);
          return;
        }
        // Touching or overlapping: grow into one span, keeping what is already
        // held where the two cover the same ground.
        const mergedStart = Math.min(span.start, from);
        const mergedEnd = Math.max(endOf(span), from + bytes.byteLength);
        const merged = new Uint8Array(mergedEnd - mergedStart);
        merged.set(bytes, from - mergedStart);
        merged.set(span.data, span.start - mergedStart);
        from = mergedStart;
        bytes = merged;
      });
      next.push({ start: from, data: bytes });
      next.sort((a, b) => a.start - b.start);
      spans = next;
    },

    /** Whether every byte of `[start, end)` is here. */
    has(start, end) {
      if (end <= start) return true;
      const at = indexFor(start);
      return at >= 0 && endOf(spans[at]) >= end;
    },

    /**
     * Bytes `[start, end)`. The caller must have checked `has` — a range with a
     * gap comes back with zeroes where nothing has arrived rather than throwing.
     */
    slice(start, end) {
      const out = new Uint8Array(Math.max(0, end - start));
      spans.forEach((span) => {
        const from = Math.max(span.start, start);
        const to = Math.min(endOf(span), end);
        if (to <= from) return;
        out.set(
          span.data.subarray(from - span.start, to - span.start),
          from - start,
        );
      });
      return out;
    },

    /** Whether the whole file is here. */
    isComplete(totalSize) {
      return (
        spans.length === 1 &&
        spans[0].start === 0 &&
        endOf(spans[0]) >= totalSize
      );
    },

    /** Everything held as one Blob, in order — only meaningful once complete. */
    toBlob(type) {
      return new Blob(
        spans.map((span) => span.data),
        type ? { type } : undefined,
      );
    },
  };
};
